import { forwardRef, useEffect, useRef, useState } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopTimePicker } from '@mui/x-date-pickers';
import { format, isValid, startOfDay } from 'date-fns';

import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg';
import { theme } from 'styles/theme';

import { TimePickerProps } from './TimePicker.types';
import { INPUT_WIDTH } from './TimePicker.styles';
import { TimeViewEnum } from './TimePicker.enum';
import * as Styled from './TimePicker.styles';

export const MINUTES_STEP_ERROR_NAME = 'minutesStep';

const formatValue = (val: Date | null): string => (val && isValid(val) ? format(val, 'HH:mm') : '');

const parseTimeText = (text: string): Date | null | undefined => {
  if (text === '') return null;
  const match = text.match(/^(\d{2}):(\d{2})$/);
  if (!match) return undefined; // incomplete / invalid text
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  if (h < 0 || h > 23 || m < 0 || m > 59) return undefined;
  return new Date(1970, 0, 1, h, m, 0);
};

export const TimePicker = forwardRef(
  (
    {
      value,
      onChange,
      disabled,
      minutesStep,
      errorMessage,
      onError,
      placeholder,
      fullWidth,
      ...props
    }: TimePickerProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentTimeView, setCurrentTimeView] = useState<TimeViewEnum>(TimeViewEnum.HOURS);
    const [inputText, setInputText] = useState(() => formatValue(value));
    const isFocusedRef = useRef(false);

    useEffect(() => {
      setCurrentTimeView(TimeViewEnum.HOURS);
    }, [isOpen]);

    // Sync text from parent value when not actively editing
    useEffect(() => {
      if (!isFocusedRef.current) {
        setInputText(formatValue(value));
      }
    }, [value]);

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      if (text.length > 5) return;
      setInputText(text);

      const parsed = parseTimeText(text);
      if (parsed !== undefined) {
        // null (cleared) or valid Date
        onChange(parsed);
      }
    };

    const handleFocus = () => {
      isFocusedRef.current = true;
    };

    const handleBlur = () => {
      isFocusedRef.current = false;
      // Reset display to the last valid formatted value
      setInputText(formatValue(value));
    };

    const displayValue = value && isValid(value) ? value : null;

    return (
      <div>
        <DesktopTimePicker
          value={displayValue}
          onChange={onChange}
          ref={ref}
          {...props}
          renderInput={(params) => (
            <div style={{ width: fullWidth ? '100%' : INPUT_WIDTH }}>
              <Styled.Input
                {...params}
                label={undefined}
                placeholder={undefined}
                ref={params.inputRef}
                rightIcon={<ClockIcon onClick={() => !disabled && setIsOpen(true)} />}
                errorMessage={errorMessage}
                disabled={disabled}
                value={inputText}
                inputProps={{
                  ...params.inputProps,
                  value: inputText,
                  onChange: handleTextChange,
                  onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                    handleFocus();
                    params.inputProps?.onFocus?.(e);
                  },
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    handleBlur();
                    params.inputProps?.onBlur?.(e);
                  },
                  placeholder,
                }}
                $isError={!!errorMessage}
                $fullWidth={fullWidth}
              />
            </div>
          )}
          PopperProps={{
            placement: 'bottom-start',
          }}
          PaperProps={{
            style: {
              backgroundColor: theme.palette.common.white,
              boxShadow: 'none',
              border: `1px solid ${theme.palette.grey[500]}`,
            },
          }}
          minutesStep={minutesStep}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          disabled={disabled}
          components={{
            PaperContent: () => (
              <div>
                <Styled.TimeButtonsWrapper>
                  <Styled.TimeButton
                    onClick={() => setCurrentTimeView(TimeViewEnum.HOURS)}
                    $isActive={currentTimeView === TimeViewEnum.HOURS}
                  >
                    {format(value && isValid(value) ? value : startOfDay(new Date()), 'HH')}
                  </Styled.TimeButton>
                  :
                  <Styled.TimeButton
                    onClick={() => setCurrentTimeView(TimeViewEnum.MINUTES)}
                    $isActive={currentTimeView === TimeViewEnum.MINUTES}
                  >
                    {format(value && isValid(value) ? value : startOfDay(new Date()), 'mm')}
                  </Styled.TimeButton>
                </Styled.TimeButtonsWrapper>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Styled.ClockPicker
                    views={['hours', 'minutes']}
                    view={currentTimeView}
                    date={!isValid(value) ? startOfDay(new Date()) : value}
                    onChange={(newDate) => onChange(newDate as Date | null)}
                    minutesStep={minutesStep}
                    onViewChange={() => setCurrentTimeView(TimeViewEnum.MINUTES)}
                  />
                </LocalizationProvider>
              </div>
            ),
          }}
        />
      </div>
    );
  },
);
